import type { Message } from '@/components/agent/types';
import type { SearchResponse, WorkflowResponse, MediaGenerateResponse } from '@/types/agent';
import { extractAllContentFromText, TAG_PATTERN, ALT_TAG_PATTERN } from './tagParser';
import { normalizeSearchData, extractSearchDataFromMessage } from './search-result-utils';

// A4: JSON 安全的深比较：替代 JSON.parse(a) === JSON.parse(b)
// 在只用于比较 skill result 中的 JSON 对象，不处理非 JSON 数据
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (Array.isArray(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

function _restoreUserMessage(m: any, base: Message) {
  if (m.attachments && m.attachments.length > 0) {
    base.attachments = m.attachments;
  } else if (m.metadata?.attachments && m.metadata.attachments.length > 0) {
    base.attachments = m.metadata.attachments;
  }
  if (m.references && m.references.length > 0) {
    base.references = m.references;
  } else if (m.metadata?.references && m.metadata.references.length > 0) {
    base.references = m.metadata.references;
  }
}

function _restoreGenerationConfirm(m: any, base: Message) {
  if ((m.metadata?.is_generation_confirm) && !m.metadata?.generation_confirmed && !m.metadata?.cancelled) {
    base.type = 'generation_confirm';
    if (m.metadata?.generation_options) base.generationOptions = m.metadata.generation_options;
    if (m.metadata?.pending_user_text) base.pendingUserText = m.metadata.pending_user_text;
    if (m.metadata?.pending_attachments) base.pendingAttachments = m.metadata.pending_attachments;
    if (m.metadata?.pending_references) base.pendingReferences = m.metadata.pending_references;
    if (m.metadata?.pending_ref_node_ids) base.pendingRefNodeIds = m.metadata.pending_ref_node_ids;
    base.text = m.content || '请确认生成参数';
    // 🔵 v3-#12: 恢复生成确认消息时也设置 introText
    base.introText = m.metadata?.intro_text || undefined;
    return true;
  }
  return false;
}

function _restoreSearchMessage(m: any, base: Message): boolean {
  if (m.type === 'search') {
    // 🔴 P7优化：使用统一的 extractSearchDataFromMessage 函数
    // 避免各模块独立实现导致的数据格式不一致
    let searchData = extractSearchDataFromMessage(m);

    // P6修复：当 extractSearchDataFromMessage 返回 null 时，尝试从 content 中解析 JSON
    // 后端某些路径可能将完整 JSON 存入 content 字段，但 JSON.parse 在 extractSearchDataFromMessage 中失败
    // （如 content 前后有多余空白/换行，或 content 是对象而非字符串）
    if (!searchData && m.content) {
      try {
        const content = typeof m.content === 'string' ? m.content.trim() : m.content;
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        if (parsed && (parsed.results || parsed.query)) {
          searchData = normalizeSearchData(parsed);
        }
      } catch {
        // content 不是有效 JSON，继续走 fallback 路径
      }
    }

    // P0修复：当 searchData 为 null 但消息类型为 search 时，
    // 创建最小化 fallback，确保搜索消息走 SearchResultsList 渲染路径
    // （使用 renderFormattedMessage 而非 TypewriterTextBlock 纯文本）
    const effectiveSearchData = searchData || {
      query: m.metadata?.optimized_query || '',
      results: [],
      summary: (typeof m.content === 'string' && m.content.trim().length > 0)
        ? m.content
        : '',
      search_type: 'general',
      _fallback: true,  // 标记为降级数据，SearchResultsList 据此跳过"未搜索到资料"提示
    };

    const rawContent = (typeof m.content === 'string' && m.content.trim().length > 0)
      ? m.content
      : (effectiveSearchData.summary && effectiveSearchData.summary.trim().length > 0)
        ? effectiveSearchData.summary
        : effectiveSearchData.query
          ? `搜索「${effectiveSearchData.query}」的结果`
          : '搜索结果';

    base.text = rawContent;
    base.type = 'search';
    base.searchData = effectiveSearchData;
    // P-F4 修复：introText 与 text 内容相同时不重复展示，避免"AI分析"区块与消息正文重复
    const candidateIntro = m.metadata?.intro_text || (typeof rawContent === 'string' && rawContent !== '搜索结果' ? rawContent : undefined);
    base.introText = (typeof base.text === 'string' && candidateIntro && base.text.trim() === candidateIntro.trim())
      ? undefined
      : candidateIntro;
    return true;
  }
  return false;
}

function _restoreWorkflowMessage(m: any, base: Message): boolean {
  if (m.type === 'workflow' && m.content) {
    try {
      const workflowData = JSON.parse(m.content) as WorkflowResponse;
      base.text = `已为您搭建工作流：${workflowData.nodes.length} 个节点`;
      base.type = 'workflow';
      base.workflowData = workflowData;
      return true;
    } catch { base.text = m.content; base.type = 'text'; }
  }
  return false;
}

function _restoreMediaMessage(m: any, base: Message): boolean {
  if ((m.type === 'image' || m.type === 'video') && m.content) {
    try {
      const mediaData = JSON.parse(m.content) as MediaGenerateResponse;
      const savedAssets = m.metadata?.saved_assets;
      if (savedAssets && mediaData.results) {
        const assetUrlMap = new Map<string, string>();
        savedAssets.forEach((a: any) => { if (a.original_url) assetUrlMap.set(a.original_url, a.asset_url); });
        mediaData.results = mediaData.results.map((r: any) => ({ ...r, url: assetUrlMap.get(r.url) || r.url }));
      }
      base.text = '';
      base.introText = m.metadata?.llm_output || '';
      // 🔴 v10-#3: 移除此行，由通用逻辑（L188-198）统一处理 processLogs 并做格式转换
      // base.processLogs = m.metadata?.process_logs || undefined;
      base.type = mediaData.taskType as Message['type'];
      base.mediaData = mediaData;
      return true;
    } catch { base.text = m.content; base.type = 'text'; }
  }
  return false;
}

function _restoreSkillResult(m: any, base: Message): boolean {
  if (!m.metadata?.skill_result || typeof m.metadata.skill_result !== 'string') return false;

  const skillStr = m.metadata.skill_result;
  // 🔴 v5-#10: 后端搜索消息 content 已改为摘要文本（v4-#7），不再包含 skill_result
  // 但非搜索类型的技能结果（如 analyze_image）仍可能追加到 content 中
  // 使用 JSON 解析后比较，避免字符串匹配的脆弱性
  if (base.text) {
    try {
      const skillParsed = JSON.parse(skillStr);
      // A4: 原先 textParsed === skillParsed 对对象/数组总是 false（引用比较），
      // 改用 deepEqual 做递归深度比较。对原始类型（string/number/boolean/null），
      // deepEqual 首行 a===b 已直接返回 true，行为与原代码一致。
      const textParsed = JSON.parse(base.text);
      if (deepEqual(textParsed, skillParsed)) {
        base.text = '';
      }
    } catch {
      // JSON 解析失败，回退到字符串匹配（但更安全）
      const suffix = '\n\n' + skillStr;
      if (base.text.endsWith(suffix)) {
        base.text = base.text.slice(0, -suffix.length);
      } else if (base.text.length > skillStr.length && base.text.includes(skillStr)) {
        base.text = base.text.replace(skillStr, '');
      }
    }
    base.text = base.text.trim();
  }

  if (!base.mediaData) {
    try {
      const skillData = JSON.parse(skillStr);
      if ((skillData.taskType === 'image' || skillData.taskType === 'video' || skillData.taskType === 'grid') && skillData.results) {
        base.type = (skillData.taskType === 'grid' ? 'image' : skillData.taskType) as Message['type'];
        base.mediaData = skillData;
        base.text = '';
      }
    } catch {}
  }
  return true;
}

function _restoreTagsFromText(m: any, base: Message): boolean {
  if (!TAG_PATTERN.test(base.text) && !ALT_TAG_PATTERN.test(base.text)) return false;

  const extracted = extractAllContentFromText(base.text);
  base.text = extracted.cleanText || base.text;

  if (extracted.processLogs.length > 0 && !base.processLogs) {
    base.processLogs = extracted.processLogs;
  }
  return true;
}

export const restoreBackendMessage = (m: {
  id: string;
  role: string;
  content: string;
  type: string;
  metadata?: Record<string, any>;
  introText?: string;
  processLogs?: any[];
  attachments?: any[];
  references?: any[];
}): Message => {
  const role = m.role === 'assistant' ? 'model' : (m.role as 'user');
  const msgType = m.type || 'text';
  const base: Message = { id: m.id, role, text: m.content || '', type: (msgType as Message['type']) || 'text' };

  if (role === 'user') {
    _restoreUserMessage(m, base);
  } else {
    if (msgType === 'generation_confirm') {
      if (!_restoreGenerationConfirm(m, base)) {
        base.type = 'text';
        base.text = m.content || '';
      }
    } else {
      _restoreSearchMessage(m, base) || _restoreWorkflowMessage(m, base) || _restoreMediaMessage(m, base);
    }

    if (m.processLogs?.length) base.processLogs = m.processLogs;
    else if (m.metadata?.process_logs) {
      base.processLogs = m.metadata.process_logs.map((log: any, i: number) => ({
        id: log.id || `log-${i}`,
        kind: log.kind || 'action',
        status: log.status || 'completed',
        title: log.title || '',
        detail: log.detail || '',
        timestamp: log.timestamp || Date.now() - (m.metadata!.process_logs.length - i) * 1000,
      }));
    }
    if (m.introText) base.introText = m.introText;
    else if (m.metadata?.intro_text) base.introText = m.metadata.intro_text;

    if (base.type === 'text' && base.text) {
      _restoreSkillResult(m, base);
      _restoreTagsFromText(m, base);
    }
  }

  if (m.attachments?.length) {
    base.attachments = m.attachments.map((item: any) => ({
      name: item.name || '附件',
      type: item.type || '',
      size: item.size || 0,
      thumbnailUrl: item.thumbnailUrl
    }));
  }
  if (m.references?.length) base.references = m.references;

  return base;
};
